import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MatchingV2Speaker } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * §7.3 of `docs/api/matching-v2.contract.md` — rerun walk-through.
 *
 *   • If `requires_attention` count ≤ 4 → render a compact header
 *     counter only («N привязок перенесены — проверить»).
 *     FE-inline emits the per-utterance banners; this component owns
 *     the global counter only — no double-render of inline UI.
 *   • If ≥ 5 → render the overlay-modal banner offering to step
 *     through one conflict at a time («Просмотреть по очереди»).
 *     Activating it opens a focus loop: page dimmed, fixed footer
 *     with «Назад / Дальше / Выйти», Esc dismisses.
 *
 * The trigger is the union of:
 *   • `preservedBindingsCount > 0` (contract §4 field, surfaces the
 *     rebuild-preserved branch);
 *   • OR any speaker with `requires_attention=true` (covers conflicts
 *     that the rebuild itself flagged).
 *
 * This component owns NO mutation logic — `onAcceptCurrent` /
 * `onRejectCurrent` come from the parent (TranscriptTab v2) so the
 * actual `/resolve` and `/unbind` calls stay in one place.
 */
export interface RerunWalkthroughProps {
  preservedBindingsCount: number;
  speakers: MatchingV2Speaker[];
  /**
   * Focus a specific speaker label inside the transcript. Walk-through
   * mode invokes this on every step so the surrounding page tracks
   * the operator's current position.
   */
  onFocusSpeaker?: (label: string) => void;
}

/** Threshold from contract §7.3 — strictly more than 4 triggers overlay. */
const OVERLAY_THRESHOLD = 5;

export function RerunWalkthrough({
  preservedBindingsCount,
  speakers,
  onFocusSpeaker,
}: RerunWalkthroughProps) {
  const { t } = useTranslation();
  // Speakers that demand operator review post-rebuild. Server marks
  // `requires_attention=true` for conflicts, rebuild-preserved bindings,
  // and unconfirmed LLM suggestions (contract §4 field-notes).
  const attentionSpeakers = useMemo(
    () => speakers.filter((s) => s.requires_attention),
    [speakers],
  );
  const conflictCount = attentionSpeakers.length;
  const isOverlayMode = conflictCount >= OVERLAY_THRESHOLD;

  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const total = attentionSpeakers.length;
  const currentSpeaker = walkthroughActive
    ? (attentionSpeakers[stepIndex] ?? null)
    : null;

  const handleNext = useCallback(() => {
    setStepIndex((i) => (total === 0 ? 0 : (i + 1) % total));
  }, [total]);

  const handlePrev = useCallback(() => {
    setStepIndex((i) => (total === 0 ? 0 : (i - 1 + total) % total));
  }, [total]);

  const handleExit = useCallback(() => {
    setWalkthroughActive(false);
    setStepIndex(0);
  }, []);

  const handleStart = useCallback(() => {
    if (attentionSpeakers.length === 0) return;
    setStepIndex(0);
    setWalkthroughActive(true);
  }, [attentionSpeakers.length]);

  // Push focus to the parent each time the step changes so the
  // surrounding transcript scrolls in sync. Effects fire after render
  // → keyboard nav stays responsive.
  useEffect(() => {
    if (currentSpeaker === null) return;
    onFocusSpeaker?.(currentSpeaker.label);
  }, [currentSpeaker, onFocusSpeaker]);

  // Esc exits walk-through. Window-level listener; nothing else on this
  // page binds Esc today, but we still gate by `walkthroughActive` so
  // unrelated dialogs keep working.
  useEffect(() => {
    if (!walkthroughActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleExit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [walkthroughActive, handleExit]);

  // Nothing to show when the rebuild produced zero attention items
  // AND zero preserved bindings.
  if (preservedBindingsCount === 0 && conflictCount === 0) return null;

  return (
    <>
      {!walkthroughActive && (
        <section
          className={cn(
            "rounded border p-3 text-sm",
            isOverlayMode
              ? "border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/30"
              : "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/30",
          )}
          data-testid="rerun-walkthrough"
          data-mode={isOverlayMode ? "overlay" : "inline-counter"}
        >
          {isOverlayMode ? (
            <OverlayBanner count={conflictCount} onStart={handleStart} />
          ) : (
            <InlineCounter count={preservedBindingsCount} />
          )}
        </section>
      )}
      {walkthroughActive && currentSpeaker !== null && (
        <WalkthroughFocus
          current={stepIndex + 1}
          total={total}
          speakerLabel={currentSpeaker.label}
          onNext={handleNext}
          onPrev={handlePrev}
          onExit={handleExit}
          ariaLabel={t("meetings.card.matchingV2.walkthrough.exitAria")}
        />
      )}
    </>
  );
}

interface InlineCounterProps {
  count: number;
}

function InlineCounter({ count }: InlineCounterProps) {
  const { t } = useTranslation();
  return (
    <span data-testid="rerun-walkthrough-counter">
      {t("meetings.card.matchingV2.walkthrough.counter", { count })}
    </span>
  );
}

interface OverlayBannerProps {
  count: number;
  onStart: () => void;
}

function OverlayBanner({ count, onStart }: OverlayBannerProps) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      data-testid="rerun-walkthrough-overlay"
    >
      <div>
        <strong className="block">
          {t("meetings.card.matchingV2.walkthrough.overlayTitle")}
        </strong>
        <span>
          {t("meetings.card.matchingV2.walkthrough.overlayBody", { count })}
        </span>
      </div>
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={onStart}
        data-testid="rerun-walkthrough-start"
      >
        {t("meetings.card.matchingV2.walkthrough.reviewButton")}
      </Button>
    </div>
  );
}

interface WalkthroughFocusProps {
  current: number;
  total: number;
  speakerLabel: string;
  onNext: () => void;
  onPrev: () => void;
  onExit: () => void;
  ariaLabel: string;
}

/**
 * Footer + page-dim overlay. The overlay sits at `z-40` (below the
 * shadcn Dialog primitive at `z-50`) so any in-flight confirmation
 * popup still floats above the walk-through chrome.
 */
function WalkthroughFocus({
  current,
  total,
  speakerLabel,
  onNext,
  onPrev,
  onExit,
  ariaLabel,
}: WalkthroughFocusProps) {
  const { t } = useTranslation();
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        aria-hidden="true"
        data-testid="rerun-walkthrough-dim"
        onClick={onExit}
      />
      <div
        role="dialog"
        aria-label={ariaLabel}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card p-3 shadow-lg"
        data-testid="rerun-walkthrough-focus"
        data-speaker-label={speakerLabel}
      >
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <span className="font-medium">
            {t("meetings.card.matchingV2.walkthrough.stepLabel", {
              current,
              total,
            })}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onPrev}
              disabled={total <= 1}
              data-testid="rerun-walkthrough-prev"
            >
              {t("meetings.card.matchingV2.walkthrough.prevButton")}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={onNext}
              disabled={total <= 1}
              data-testid="rerun-walkthrough-next"
            >
              {t("meetings.card.matchingV2.walkthrough.nextButton")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onExit}
              aria-label={ariaLabel}
              data-testid="rerun-walkthrough-exit"
            >
              {t("meetings.card.matchingV2.walkthrough.exitButton")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
